/**
 * `emitMultiAddAnalytics` — single entry point for every Multi-Add Phase 2
 * analytics emission.
 *
 * Why one helper instead of inline `track()` calls:
 *
 * 1. **Single mock point.** Tests stub one symbol (this helper); call sites
 *    (`wiring.ts`, `onvif-auth-prompt.tsx`, the notification panel) stay
 *    independent of the analytics provider stack. Single point of test
 *    mocking — grep for direct PostHog `track()` in `add/` returns no
 *    results.
 *
 * 2. **Canonical property shape.** The helper accepts `MultiAddProperties`
 *    typed inputs and normalizes them into the `EventProperties` shape the
 *    underlying `track()` consumer expects. Per-event property contracts
 *    live in `analytics/types/properties.ts` — this helper does not invent
 *    new keys.
 *
 * 3. **Debounce + category routing.** The helper threads through the
 *    debounce-capped `track()` from `utils/debounced-track.ts`, which reads
 *    `analytics/config/debounce-config.ts` to decide per-event behavior
 *    (per CEO §3 risk flag: per-device events get medium-frequency dedupe
 *    to cap PostHog cost on large batches; batch-level events are
 *    `NO_DEBOUNCE_CONFIG` so funnel keys survive).
 *
 * 4. **Structured `logger` mirror.** Every emission also pings the canonical
 *    `logger` at `info` level so the same outcome appears in Sentry-tracked
 *    structured logs alongside the PostHog event. This replaces P1's
 *    `console.warn` placeholders inside `runBulkCommit` — per
 *    `use-canonical-logger-not-console` (ai.knowledge/platform/studio.client):
 *    raw `console.*` calls are debug residue, never production telemetry.
 *
 * Per `think-in-systems` (ai.knowledge/global): the helper is the primitive;
 * every multi-add call site composes over it.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import { EVENT_CATEGORIES } from '../constants/event-categories.js';
import { EVENT_NAMES, type EventName } from '../constants/event-names.js';
import { logger } from '#~/logger/index.js';
import type { EventProperties, MultiAddProperties } from '../types/properties.js';
import { track } from './debounced-track.js';

/**
 * Subset of EVENT_NAMES that are valid for `emitMultiAddAnalytics`. This
 * is intentionally a closed enum — only the catalog members from PRD §12
 * plus the existing P1 stubs ship through this helper. Other events keep
 * using the standard `track()`/`useAnalytics()` hook.
 *
 * All 11 event names are declared in `analytics/constants/event-names.ts`
 * as named `EVENT_NAMES.*` constants — never inline strings. The literal
 * type below enforces that constraint at compile time.
 */
export type MultiAddEventName =
  | typeof EVENT_NAMES.MULTI_ADD_STARTED
  | typeof EVENT_NAMES.MULTI_ADD_COMPLETED
  | typeof EVENT_NAMES.MULTI_ADD_PARTIAL_SUCCESS
  | typeof EVENT_NAMES.MULTI_ADD_DEVICE_ADDED
  | typeof EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION
  | typeof EVENT_NAMES.MULTI_ADD_CHIP_CLICKED
  // Multi-Add Phase 2 — discovery-table multi-select checkbox toggle.
  | typeof EVENT_NAMES.MULTI_ADD_DISCOVERY_SELECTED
  | typeof EVENT_NAMES.MULTI_ADD_NOTIFICATION_CLICKED
  | typeof EVENT_NAMES.DEVICE_CONFIGURATION_COMPLETED_FROM_NOTIFICATION
  | typeof EVENT_NAMES.SINGLE_ADD_STARTED
  | typeof EVENT_NAMES.SINGLE_ADD_COMPLETED
  | typeof EVENT_NAMES.ADD_SOURCE_MODAL_OPENED
  | typeof EVENT_NAMES.ADD_SOURCE_MODAL_CANCELLED
  // Multi-Add Phase 2 — explicit batch cancellation event.
  | typeof EVENT_NAMES.MULTI_ADD_BATCH_CANCELLED
  // Multi-Add Phase 2 — per-(device, sub-tab) needs-attention icon
  // first surfaced. Emitted exactly once per session per (device, tab)
  // by the `useNeedAttentionTabs` hook on each false→true transition,
  // deduped via the depot's `shownSubtabAnalytics` slice.
  | typeof EVENT_NAMES.MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN
  // ENG-4486 — fire-side post-add alert. Emitted by the
  // `useMultiAddBucketNotifications` reconciliation on each desired-alert
  // transition (once per (device, chip_type) by construction). Carries the
  // PRD §13 `chip_type` (incl. `multiple` for the combined alert).
  | typeof EVENT_NAMES.MULTI_ADD_ALERT_SHOWN;

/**
 * Props accepted by the helper. Beyond the multi-add specific keys
 * (`batchId`, counts, outcomes), every emission may carry the standard
 * device / studio / brand / model context — these are routed through the
 * generic `EventProperties` shape so PostHog dashboards can drill into a
 * single batch by device id without bespoke per-event mappings.
 *
 * Unit tests mock `track`, assert each event fires with canonical
 * property shape (using `EVENT_NAMES.*` constants, not strings).
 */
export type MultiAddEmitProps = MultiAddProperties &
  Pick<
    EventProperties,
    | 'deviceId'
    | 'deviceName'
    | 'deviceType'
    | 'brand'
    | 'model'
    | 'controlProtocol'
    | 'studioId'
    | 'sourceProtocol'
    | 'sourceType'
  >;

/**
 * Shape of `LinkIrisResult` (from `video/components/add/source/link-iris.ts`)
 * — recopied here to keep this helper import-free of the bulk-add pipeline.
 * If the source-of-truth drifts, the unit tests will catch it via the
 * `mapLinkOutcome` table-driven test.
 *
 * Per `think-in-systems` (ai.knowledge/global): one-way dependency. Analytics
 * helpers must not pull renderer types into the analytics tree — that would
 * invert the layer ordering.
 */
type LinkIrisResultShape = Readonly<
  | { kind: 'success'; agentId?: unknown }
  | {
      kind: 'failed';
      reason: 'locked' | 'auth' | 'already-linked' | 'error';
      message?: string;
      lockoutSeconds?: number;
    }
>;

/**
 * Map a `LinkIrisResult` into the canonical `link_outcome` enum.
 *
 * Per `state-sync-optimistic-mutations` (ai.knowledge/platform/studio.client):
 * the mapping happens AFTER `linkIrisDevice` resolves (which awaits the
 * underlying `proxy.mutate` → `linkAgent` round trip); by that time the
 * local agent fleet already reflects the link, so the emission is safe to
 * fire synchronously with no race against subsequent state-sync reads.
 *
 * The hyphenated reason `already-linked` is normalized to the underscored
 * PostHog identifier `already_linked` — PostHog property values prefer
 * underscores for consistency with other event enums (e.g. `auto_link_*`).
 *
 * link_outcome ∈ {success, locked, auth, already_linked, error} maps
 * cleanly from LinkIrisResult.kind/reason.
 */
export const mapLinkOutcome = (
  result: LinkIrisResultShape
): NonNullable<MultiAddProperties['linkOutcome']> => {
  if (result.kind === 'success') {
    return 'success';
  }
  if (result.reason === 'already-linked') {
    return 'already_linked';
  }
  return result.reason;
};

/**
 * Result shape produced by the inline ONVIF auth retry state machine in
 * `runBulkCommit`. Five mutually-exclusive outcomes; the helper consumes
 * one of them and emits the matching enum value.
 *
 * probe_outcome ∈ {success_first_try, success_after_prompt,
 * skipped_by_user, skipped_after_retry, timeout} maps from the inline
 * auth retry state machine.
 */
export type ProbeOutcomeInput =
  | { kind: 'success'; afterPrompt: boolean }
  | { kind: 'skipped'; afterRetry: boolean }
  | { kind: 'timeout' };

/**
 * Map a probe state-machine result into the canonical `probe_outcome` enum.
 *
 * Branching:
 *  - success + no prompt → first-try (the discovery already carried creds).
 *  - success + prompt    → after-prompt (user typed creds, retry probe OK).
 *  - skipped + !retried  → user clicked Skip directly.
 *  - skipped +  retried  → user typed creds but the retry probe still failed.
 *  - timeout             — probe never completed (caller decides this; the
 *                          retry state machine in P1 currently rolls timeout
 *                          into `skipped_after_retry`, but the enum carves
 *                          out timeout explicitly for future use).
 */
export const mapProbeOutcome = (
  input: ProbeOutcomeInput
): NonNullable<MultiAddProperties['probeOutcome']> => {
  if (input.kind === 'timeout') return 'timeout';
  if (input.kind === 'success') {
    return input.afterPrompt ? 'success_after_prompt' : 'success_first_try';
  }
  return input.afterRetry ? 'skipped_after_retry' : 'skipped_by_user';
};

/**
 * Emit a Multi-Add Phase 2 analytics event.
 *
 * Every call site MUST use this helper rather than calling `track()` directly
 * — see file header rationale #1.
 *
 * The helper is intentionally fire-and-forget (returns `void`, not a
 * Promise). The underlying `track()` returns a Promise, but call sites in
 * `runBulkCommit` execute inside a hot loop where awaiting analytics would
 * stall the per-device commit pipeline. Errors inside `track()` are caught
 * and logged at the provider layer (`analytics/track.ts:31`).
 *
 * @param event  — A name from the closed `MultiAddEventName` enum.
 * @param props  — Optional properties. Omit when the event has no payload
 *   (e.g. `add_source_modal_opened`).
 */
export const emitMultiAddAnalytics = (
  event: MultiAddEventName,
  props?: MultiAddEmitProps
): void => {
  // Cast widening: MultiAddEmitProps ⊂ EventProperties via the merge in
  // properties.ts. The track API accepts EventProperties — passing the
  // narrower type through is type-safe at the call boundary.
  const eventProperties: EventProperties | undefined = props as EventProperties | undefined;

  // Per `use-canonical-logger-not-console` (ai.knowledge/platform/studio.client):
  // replace P1's `console.warn` placeholders with structured logger.info
  // emissions alongside the PostHog event. This gives operators a Sentry
  // trail when triaging "why did this batch only land 2 of 5 sources".
  // `logger.info` is the right level — these are normal operational
  // events, not warnings (the skipped/failed states are already shaped
  // into the event payload).
  logger.info(`multi-add analytics: ${event}`, eventProperties ?? {});

  // VIDEO_SOURCES is the canonical category for every Multi-Add event —
  // see types/events.ts VideoSourcesEvent union. `track` (from
  // utils/debounced-track.ts) routes through the spam-protection layer
  // and the configured PostHog provider stack.
  // Per `reference-rules-in-comments` (ai.knowledge/global) —
  // think-in-systems: every emit funnels through one primitive.
  void track(event as EventName, EVENT_CATEGORIES.VIDEO_SOURCES, eventProperties);
};
